import type { ReactNode } from "react";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MONTH_NAMES, type useCalendarMonths } from "./useCalendarMonths";

interface CalendarNavigationProps {
  calendar: ReturnType<typeof useCalendarMonths>;
  legend: ReactNode;
  /** Extra desktop buttons after "Today" (e.g. a people filter) */
  extraButtons?: ReactNode;
  /** Actions on the right, after the year */
  headerActions?: ReactNode;
}

/** Navigation bar of the month-column calendars. */
export function CalendarNavigation({
  calendar,
  legend,
  extraButtons,
  headerActions,
}: CalendarNavigationProps) {
  const {
    isMobile,
    startDate,
    visibleStartMonth,
    visibleEndMonth,
    handlePrevious,
    handleNext,
    handleToday,
    handleMonthChange,
    handleYearChange,
  } = calendar;

  const legendButton = (align: "start" | "end") => (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <List className="h-4 w-4 mr-1" />
          Legend
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align={align}>
        {legend}
      </PopoverContent>
    </Popover>
  );

  return (
    <div className="flex items-center justify-between mb-4 px-2">
      {isMobile ? (
        <>
          <Button variant="outline" size="icon" onClick={handlePrevious}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Select
              value={startDate.getMonth().toString()}
              onValueChange={handleMonthChange}
            >
              <SelectTrigger className="w-[110px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTH_NAMES.map((name, index) => (
                  <SelectItem key={index} value={index.toString()}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={startDate.getFullYear().toString()}
              onValueChange={handleYearChange}
            >
              <SelectTrigger className="w-[80px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="icon" onClick={handleNext}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          {legendButton("end")}
        </>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevious}
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleToday}>
              Today
            </Button>
            {extraButtons}
            {legendButton("start")}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {format(visibleStartMonth, "yyyy")}
              {visibleStartMonth.getFullYear() !==
                visibleEndMonth.getFullYear() &&
                ` - ${format(visibleEndMonth, "yyyy")}`}
            </span>
            {headerActions}
          </div>
        </>
      )}
    </div>
  );
}
